<script>
/**
 * @param manifest { Object }
 * @property manifest.id { Number } マニフェストID
 * @property manifest.enable_copy { Boolean } 複写ボタン表示対象
 * @property manifest.target_for_registration { Boolean } 登録対象
 * @property manifest.disposal_report_type { String } 報告区分
 * @property manifest.disposal_report_person { String } 処分担当者
 * @property manifest.disposal_report_end_date { String } 処分終了日
 * @property manifest.disposal_report_reporter { String } 報告担当者
 * @property manifest.disposal_report_receipt_date { String } 廃棄物受領日
 * @property manifest.disposal_report_remarks { String } 処分備考
 */
export default {
  name: "DisposalBasicInformation",
  components: {},
  props: {
    /**
     * マニフェスト
     */
    manifest: {
      type: Object,
      default: () => ({}),
    },
    isFirstField: {
      type: Boolean,
      default: () => false,
    },
    callerType: {
      type: String,
      default: () => null,
    },
    users: {
      type: Array,
      default: () => [],
    },
    quantityUnits: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      systemDate: this.$dayjs().format("YYYY-MM-DD"),
      reportCategories: [
        { value: 'middle_report', text: '中間' },
        { value: 'last_report', text: '最終' }
      ],
    }
  },
  created() {},
  methods: {
    /**
     * 複写ボタン クリックイベント
     */
    copyFieldButtonClickEvent (property, value) {
      this.$emit("copyField", property, value);
    },

    copyFullFieldButtonClickEvent() {
      this.$emit("copyAllField", this.manifest)
    },
    disposalReportEndDateChangedCallback (date) {
      this.manifest.disposal_report_end_date = date;
    },
    disposalReportReceiptDateChangedCallback (date) {
      this.manifest.disposal_report_receipt_date = date;
    }
  },
}
</script>

<template>
  <div class="reportLayer">
    <v-row dense class="pb-3">
      <v-col cols="4">
        <h3>基本情報</h3>
      </v-col>
      <v-col
        v-if="isFirstField"
        cols="8"
      >
        <v-btn
          :disabled="!manifest.target_for_registration"
          block
          color="primary"
          small
          @click="copyFullFieldButtonClickEvent"
        >
          基本情報複写
          &nbsp;<v-icon x-small>mdi-chevron-double-down</v-icon>
        </v-btn>
      </v-col>
    </v-row>

    <v-row dense >
      <!-- 報告区分 -->
      <v-col cols="6">
        <div class="field-with-copy">
          <validation-provider
            v-slot="{ errors }"
            :disabled="!manifest.target_for_registration"
            :vid="`disposal_report_type-${manifest.id}`"
            name="報告区分"
            class="field-with-copy__control"
          >
            <v-radio-group
              v-model="manifest.disposal_report_type"
              :error-messages="errors"
              row
              hide-details="auto"
              class="mt-0"
            >
              <v-radio
                v-for="n in reportCategories"
                :disabled="!manifest.target_for_registration"
                :key="n.value"
                :label="n.text"
                :value="n.value"
              />
            </v-radio-group>
          </validation-provider>
          <div
            v-if="callerType !== 'bulk' && isFirstField"
            class="copy-actions mt-1"
          >
            <v-btn
              v-if="isFirstField"
              :disabled="!manifest.target_for_registration"
              color="primary"
              small
              @mousedown.stop.prevent
              @click.stop="copyFieldButtonClickEvent('disposal_report_type', manifest.disposal_report_type)"
            >
              <v-icon>mdi-chevron-double-down</v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
      <!-- /報告区分 -->

      <!-- 処分担当者 -->
      <v-col cols="6">
        <div class="field-with-copy">
          <validation-provider
            v-slot="{ errors }"
            :disabled="!manifest.target_for_registration"
            :vid="`disposal_report_person-${manifest.id}`"
            name="処分担当者"
            class="requiredMark field-with-copy__control"
            rules="required|max:12"
          >
            <v-combobox
              v-model="manifest.disposal_report_person"
              :disabled="!manifest.target_for_registration"
              :error-messages="errors"
              :items="users"
              :return-object="false"
              dense
              label="処分担当者"
              hide-details="auto"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>処分担当者</span>
                <span class="ml-1">
                    ({{ manifest.disposal_report_person?.length || 0 }} / 12)
                  </span>
              </template>
            </v-combobox>
          </validation-provider>
          <div
            v-if="callerType !== 'bulk' && isFirstField"
            class="copy-actions"
          >
            <v-btn
              v-if="isFirstField"
              :disabled="!manifest.target_for_registration"
              color="primary"
              small
              @mousedown.stop.prevent
              @click.stop="copyFieldButtonClickEvent('disposal_report_person', manifest.disposal_report_person)"
            >
              <v-icon>mdi-chevron-double-down</v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
      <!-- /処分担当者 -->

    </v-row>

    <v-row dense>
      <!-- 処分終了日 -->
      <v-col cols="6">
        <cs-date-picker
          :date.sync="manifest.disposal_report_end_date"
          :disabled="!manifest.target_for_registration"
          :minimum-date="manifest.collected_date ? manifest.collected_date : null"
          :maximum-date="systemDate"
          @dateChangedCallback="disposalReportEndDateChangedCallback"
        >
          <template v-slot="{ on, attrs }">
            <div class="field-with-copy">
              <validation-provider
                v-slot="{ errors }"
                :disabled="!manifest.target_for_registration"
                :vid="`disposal_report_end_date-${manifest.id}`"
                class="requiredMark field-with-copy__control"
                name="処分終了日"
                rules="required"
              >
                <v-text-field
                  v-model="manifest.disposal_report_end_date"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!manifest.target_for_registration"
                  :error-messages="errors"
                  append-icon="mdi-calendar"
                  clearable
                  dense
                  hide-details="auto"
                  label="処分終了日"
                  outlined
                  persistent-placeholder
                  readonly
                />
              </validation-provider>
              <div
                v-if="callerType !== 'bulk' && isFirstField"
                class="copy-actions"
              >
                <v-btn
                  v-if="isFirstField"
                  :disabled="!manifest.target_for_registration"
                  color="primary"
                  small
                  @mousedown.stop.prevent
                  @click.stop="copyFieldButtonClickEvent('disposal_report_end_date', manifest.disposal_report_end_date)"
                >
                  <v-icon>mdi-chevron-double-down</v-icon>
                </v-btn>
              </div>
            </div>
          </template>
        </cs-date-picker>
      </v-col>
      <!-- /処分終了日 -->

      <!-- 報告担当者 -->
      <v-col cols="6">
        <div class="field-with-copy">
          <validation-provider
            v-slot="{ errors }"
            :vid="`disposal_report_reporter-${manifest.id}`"
            :disabled="!manifest.target_for_registration"
            name="報告担当者"
            rules="max:12"
            class="field-with-copy__control"
          >
            <v-combobox
              v-model="manifest.disposal_report_reporter"
              :disabled="!manifest.target_for_registration"
              :error-messages="errors"
              :items="users"
              :return-object="false"
              dense
              label="報告担当者"
              hide-details="auto"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>報告担当者</span>
                <span class="ml-1">
                    ({{ manifest.disposal_report_reporter?.length || 0 }} / 12)
                  </span>
              </template>
              <template v-slot:counter></template>
            </v-combobox>
          </validation-provider>
          <div
            v-if="callerType !== 'bulk' && isFirstField"
            class="copy-actions"
          >
            <v-btn
              v-if="isFirstField"
              :disabled="!manifest.target_for_registration"
              color="primary"
              small
              @mousedown.stop.prevent
              @click.stop="copyFieldButtonClickEvent('disposal_report_reporter', manifest.disposal_report_reporter)"
            >
              <v-icon>mdi-chevron-double-down</v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
      <!-- /報告担当者 -->

    </v-row>

    <v-row dense>
      <!-- 廃棄物受領日 -->
      <v-col cols="6">
        <cs-date-picker
          :date.sync="manifest.disposal_report_receipt_date"
          :disabled="!manifest.target_for_registration"
          :minimum-date="manifest.collected_date ? manifest.collected_date : null"
          :maximum-date="systemDate"
          @dateChangedCallback="disposalReportReceiptDateChangedCallback"
        >
          <template v-slot="{ on, attrs }">
            <div class="field-with-copy">
              <validation-provider
                v-slot="{ errors }"
                :disabled="!manifest.target_for_registration"
                :vid="`disposal_report_receipt_date-${manifest.id}`"
                name="廃棄物受領日"
                :rules="{ disposalReportReceiptDateCheck: { disposal_report_end_date: manifest.disposal_report_end_date } }"
                class="field-with-copy__control"
              >
                <v-text-field
                  v-model="manifest.disposal_report_receipt_date"
                  v-bind="attrs"
                  v-on="on"
                  :disabled="!manifest.target_for_registration"
                  :error-messages="errors"
                  append-icon="mdi-calendar"
                  clearable
                  dense
                  hide-details="auto"
                  label="廃棄物受領日"
                  outlined
                  persistent-hint
                  persistent-placeholder
                  readonly
                />
              </validation-provider>
              <div
                v-if="callerType !== 'bulk' && isFirstField"
                class="copy-actions"
              >
                <v-btn
                  v-if="isFirstField"
                  :disabled="!manifest.target_for_registration"
                  color="primary"
                  small
                  @mousedown.stop.prevent
                  @click.stop="copyFieldButtonClickEvent('disposal_report_receipt_date', manifest.disposal_report_receipt_date)"
                >
                  <v-icon>mdi-chevron-double-down</v-icon>
                </v-btn>
              </div>
            </div>
          </template>
        </cs-date-picker>
      </v-col>
      <!-- /廃棄物受領日 -->

      <!-- 処分備考 -->
      <v-col cols="12">
        <div class="field-with-copy">
          <validation-provider
            v-slot="{ errors }"
            :disabled="!manifest.target_for_registration"
            :vid="`disposal_report_remarks-${manifest.id}`"
            name="処分備考"
            rules="max:128"
            class="field-with-copy__control"
          >
            <v-text-field
              v-model="manifest.disposal_report_remarks"
              :disabled="!manifest.target_for_registration"
              :error-messages="errors"
              dense
              hide-details="auto"
              label="処分備考"
              outlined
              persistent-placeholder
            >
              <template v-slot:label>
                <span>処分備考</span>
                <span class="ml-1">
                    ({{ manifest.disposal_report_remarks?.length || 0 }} / 128)
                  </span>
              </template>
              <template v-slot:counter></template>
            </v-text-field>
          </validation-provider>
          <div
            v-if="callerType !== 'bulk' && isFirstField"
            class="copy-actions"
          >
            <v-btn
              v-if="isFirstField"
              :disabled="!manifest.target_for_registration"
              color="primary"
              small
              @mousedown.stop.prevent
              @click.stop="copyFieldButtonClickEvent('disposal_report_remarks', manifest.disposal_report_remarks)"
            >
              <v-icon>mdi-chevron-double-down</v-icon>
            </v-btn>
          </div>
        </div>
      </v-col>
      <!-- 処分備考 -->
    </v-row>
  </div>
</template>

<style lang="scss">
.field-with-copy {
  display: flex;
  align-items: flex-start;
}

.field-with-copy__control {
  flex: 1;
  min-width: 0;
}

.copy-actions {
  padding-top: 4px;
}

</style>
